import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DateInput } from '../components/ui/date-input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, Receipt, Copy, ArrowUpDown, ArrowUp, ArrowDown, Download, User, Phone, Mail, MapPin, X, Settings, Clock, CheckCircle, Loader2, Send, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = { da_pagare: 'Da Pagare', pagato: 'Pagato', scaduto: 'Scaduto', attivo: 'Attivo', cessato: 'Cessato' };
const paymentMethods = { bonifico: 'Bonifico', bonifico_droppoint: 'Bonifico DropPoint', carta: 'Carta', rid: 'RID', contanti: 'Contanti' };

const emptyBilling = {
  contract_id: '',
  client_id: '',
  amount: '',
  billing_date: '',
  due_date: '',
  period_from: '',
  period_to: '',
  status: 'da_pagare',
  payment_date: '',
  payment_method: '',
  notes: ''
};

// Helper: formatta "YYYY-MM-DD" o "YYYY-MM" come "mese aa" (es. "feb 26")
const formatPeriodShort = (isoDate) => {
  if (!isoDate) return '';
  const months = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
  const m = isoDate.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const monthName = months[parseInt(m[2]) - 1];
  const year = m[1].slice(-2);
  return `${monthName} ${year}`;
};

// Helper: formatta range "Feb 26 - Apr 26"
const formatPeriodRange = (from, to) => {
  if (!from && !to) return '—';
  const f = formatPeriodShort(from);
  const t = formatPeriodShort(to);
  if (f && t && f === t) return f;
  if (f && t) return `${f} - ${t}`;
  return f || t || '—';
};

// Helper: genera opzioni Mese-Anno per i select periodo
const generateMonthOptions = (yearsRange = 5) => {
  const months = [
    { num: 1, name: 'Gennaio' }, { num: 2, name: 'Febbraio' }, { num: 3, name: 'Marzo' },
    { num: 4, name: 'Aprile' }, { num: 5, name: 'Maggio' }, { num: 6, name: 'Giugno' },
    { num: 7, name: 'Luglio' }, { num: 8, name: 'Agosto' }, { num: 9, name: 'Settembre' },
    { num: 10, name: 'Ottobre' }, { num: 11, name: 'Novembre' }, { num: 12, name: 'Dicembre' },
  ];
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let y = currentYear - 2; y <= currentYear + yearsRange; y++) {
    for (const m of months) {
      const mm = String(m.num).padStart(2, '0');
      options.push({
        value: `${y}-${mm}-01`,
        label: `${m.name} ${y}`,
      });
    }
  }
  return options;
};

const MONTH_OPTIONS = generateMonthOptions();

// Helper: calcola ultimo giorno del mese da YYYY-MM-01
const getLastDayOfMonth = (isoDate) => {
  if (!isoDate) return '';
  const m = isoDate.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const year = parseInt(m[1]);
  const month = parseInt(m[2]);
  const lastDay = new Date(year, month, 0).getDate();
  return `${m[1]}-${m[2]}-${String(lastDay).padStart(2, '0')}`;
};

const TelecomBilling = () => {
  const [billings, setBillings] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBilling, setEditingBilling] = useState(null);
  const [formData, setFormData] = useState(emptyBilling);
  const [amountSort, setAmountSort] = useState('none');
  const [dateSort, setDateSort] = useState('none');
  const [dueDateSort, setDueDateSort] = useState('none');
  const [statusSort, setStatusSort] = useState('none');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 30;

  // Client details dialog
  const [clientDetailsOpen, setClientDetailsOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [editingClient, setEditingClient] = useState(false);
  const [clientFormData, setClientFormData] = useState({});

  // Email config state
  const [emailConfigOpen, setEmailConfigOpen] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);

  const handleAutoGenerate = async () => {
    if (!window.confirm("Vuoi generare automaticamente le prossime fatture per quelle scadute? Le fatture vengono create solo se non esiste già la successiva per il contratto.")) return;
    setAutoGenerating(true);
    try {
      const res = await axios.post(`${API}/telecom/billing/auto-generate-next`);
      const d = res.data;
      if (d.total_created > 0) {
        toast.success(`Create ${d.total_created} nuove fatture. (Saltate ${d.skipped_already_exists} gia' esistenti, ${d.skipped_contract_inactive} contratti cessati/sospesi)`);
        fetchBillings();
      } else {
        toast.info(`Nessuna nuova fattura generata. Saltate ${d.skipped_already_exists} gia' esistenti, ${d.skipped_contract_inactive} contratti inattivi.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella generazione');
    } finally { setAutoGenerating(false); }
  };
  const [emailConfig, setEmailConfig] = useState({
    enabled: false,
    sender_email: 'no-reply@jvservice.it',
    sender_name: 'JV Service - Ufficio Telefonia',
    daily_send_time: '09:00',
    days_before_expiry: 30,
    email_subject: 'Scadenza Contratto Telefonico - {operator} - JV Service',
    email_body: ''
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [expandedClients, setExpandedClients] = useState({});
  // Arrivo da Telefonia → Scadenze (?fattura=ID): filtra sul cliente, apre il suo gruppo ed
  // evidenzia la fattura
  const [searchParams, setSearchParams] = useSearchParams();
  const [evidenziata, setEvidenziata] = useState(null);
  const [clientSort, setClientSort] = useState('desc'); // desc = più recente prima

  useEffect(() => { fetchData(); fetchEmailConfig(); }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, clientSort]);

  const fetchData = async () => {
    try {
      const [billingsRes, contractsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/telecom/billing`),
        axios.get(`${API}/telecom/contracts`),
        axios.get(`${API}/clients`)
      ]);
      setBillings(billingsRes.data);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { 
        ...formData, 
        amount: formData.amount ? parseFloat(formData.amount) : 0 
      };
      if (editingBilling) {
        await axios.put(`${API}/telecom/billing/${editingBilling.id}`, payload);
        toast.success('Fattura aggiornata');
      } else {
        await axios.post(`${API}/telecom/billing`, payload);
        toast.success('Fattura creata');
      }
      setDialogOpen(false);
      setEditingBilling(null);
      setFormData(emptyBilling);
      setClientSearch('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleEdit = (billing) => {
    setEditingBilling(billing);
    setFormData({
      contract_id: billing.contract_id || '',
      client_id: billing.client_id || '',
      amount: billing.amount ? billing.amount.toString() : '',
      billing_date: billing.billing_date || '',
      due_date: billing.due_date || '',
      period_from: billing.period_from || '',
      period_to: billing.period_to || '',
      status: billing.status || 'da_pagare',
      payment_date: billing.payment_date || '',
      payment_method: billing.payment_method || '',
      notes: billing.notes || ''
    });
    setDialogOpen(true);
  };

  const handleClone = (billing) => {
    setEditingBilling(null);
    setFormData({
      contract_id: billing.contract_id || '',
      client_id: billing.client_id || '',
      amount: billing.amount ? billing.amount.toString() : '',
      billing_date: '', // Reset date for new billing
      due_date: '',
      status: 'da_pagare',
      payment_date: '',
      payment_method: billing.payment_method || '',
      notes: billing.notes || ''
    });
    setDialogOpen(true);
    toast.info('Fattura clonata - modifica i dati e salva');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa fattura?')) return;
    try {
      await axios.delete(`${API}/telecom/billing/${id}`);
      toast.success('Fattura eliminata');
      fetchData();
    } catch (error) {
      toast.error('Errore');
    }
  };

  // Client details functions
  const openClientDetails = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setSelectedClient(client);
      setClientFormData({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        postal_code: client.postal_code || ''
      });
      setEditingClient(false);
      setClientDetailsOpen(true);
    }
  };

  const handleSaveClient = async () => {
    try {
      await axios.put(`${API}/clients/${selectedClient.id}`, clientFormData);
      toast.success('Cliente aggiornato');
      setEditingClient(false);
      fetchData();
      // Update selected client
      setSelectedClient(prev => ({ ...prev, ...clientFormData }));
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  const formatDate = (date) => date ? new Date(date).toLocaleDateString('it-IT') : '-';

  const getStatusBadge = (status) => {
    const classes = { 
      da_pagare: 'bg-amber-100 text-amber-700', 
      pagato: 'bg-emerald-100 text-emerald-700', 
      scaduto: 'bg-red-100 text-red-700',
      attivo: 'bg-blue-100 text-blue-700',
      cessato: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={classes[status] || 'bg-slate-100 text-slate-700'}>{statusLabels[status] || status}</Badge>;
  };

  const handleMarkPaid = async (billing, generateNext = true) => {
    try {
      const response = await axios.post(`${API}/telecom/billing/${billing.id}/mark-paid?generate_next=${generateNext}`);
      if (response.data.next_billing_generated) {
        toast.success('Fattura pagata - Prossima fattura generata automaticamente');
      } else {
        toast.success('Fattura marcata come pagata');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleSendEmail = async (billing) => {
    try {
      const response = await axios.post(`${API}/telecom/billing/${billing.id}/send-email`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    }
  };

  const fetchEmailConfig = async () => {
    try {
      const res = await axios.get(`${API}/telecom/email-config`);
      setEmailConfig(res.data);
    } catch (e) {}
  };

  const saveEmailConfig = async () => {
    setConfigLoading(true);
    try {
      await axios.put(`${API}/telecom/email-config`, emailConfig);
      toast.success('Configurazione salvata');
      setEmailConfigOpen(false);
    } catch (e) {
      toast.error('Errore nel salvataggio');
    } finally {
      setConfigLoading(false);
    }
  };

  const toggleAmountSort = () => {
    setDateSort('none');
    setDueDateSort('none');
    setStatusSort('none');
    setAmountSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  const toggleDateSort = () => {
    setAmountSort('none');
    setDueDateSort('none');
    setStatusSort('none');
    setDateSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  const toggleDueDateSort = () => {
    setAmountSort('none');
    setDateSort('none');
    setStatusSort('none');
    setStatusSort('none');
    setDueDateSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  const toggleStatusSort = () => {
    setAmountSort('none');
    setDateSort('none');
    setDueDateSort('none');
    setStatusSort(prev => {
      if (prev === 'none') return 'asc';
      if (prev === 'asc') return 'desc';
      return 'none';
    });
  };

  // Nel form fattura solo i clienti con almeno un contratto di telefonia (più quello già
  // selezionato, per le fatture vecchie da modificare)
  const clientiConContratto = new Set(contracts.map(c => c.client_id));

  // Filter clients for search in form (token-based: ogni parola cercata indipendentemente)
  const filteredClients = clients.filter(client => {
    if (!clientiConContratto.has(client.id) && client.id !== formData.client_id) return false;
    if (!clientSearch) return true;
    const searchable = [
      client.first_name,
      client.last_name,
      client.business_name,
      client.ragione_sociale,
      client.codice_fiscale,
      client.piva,
      client.client_code,
    ].filter(Boolean).join(' ').toLowerCase();
    const tokens = clientSearch.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.every(t => searchable.includes(t));
  });

  // Arrivo da Contratti (?nuova=ID contratto): nuova fattura già compilata dai dati del
  // contratto — importo = canone scontato × mesi della frequenza (stessa regola delle fatture
  // esistenti), periodo che continua dall'ultima fattura del contratto (o dall'inizio del
  // contratto), data oggi e scadenza a 15 giorni. L'operatore controlla e salva.
  useEffect(() => {
    const contractId = searchParams.get('nuova');
    if (!contractId || loading) return;
    setSearchParams({}, { replace: true });
    const c = contracts.find((x) => x.id === contractId);
    if (!c) {
      toast.error('Contratto non trovato');
      return;
    }
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const mesi = { mensile: 1, bimestrale: 2, trimestrale: 3, semestrale: 6, annuo: 12 }[c.payment_frequency] || 1;
    const importo = Math.round((c.monthly_cost || 0) * (1 - (c.sconto || 0) / 100) * mesi * 100) / 100;
    const precedenti = billings
      .filter((b) => b.contract_id === c.id)
      .sort((a, b) => (b.period_to || b.billing_date || '').localeCompare(a.period_to || a.billing_date || ''));
    const ultima = precedenti[0];
    let inizio;
    if (ultima?.period_to) {
      inizio = new Date(`${ultima.period_to}T12:00:00`);
      inizio.setDate(inizio.getDate() + 1);
    } else {
      inizio = c.start_date ? new Date(`${c.start_date}T12:00:00`) : new Date();
    }
    const fine = new Date(inizio);
    fine.setMonth(fine.getMonth() + mesi);
    fine.setDate(fine.getDate() - 1);
    const oggi = new Date();
    const scadenza = new Date(oggi);
    scadenza.setDate(scadenza.getDate() + 15);
    if (precedenti.some((b) => b.period_from === iso(inizio))) {
      toast.warning('Esiste già una fattura per questo periodo: controlla prima di salvare');
    }
    setEditingBilling(null);
    setFormData({
      ...emptyBilling,
      contract_id: c.id,
      client_id: c.client_id,
      amount: importo ? importo.toString() : '',
      billing_date: iso(oggi),
      due_date: iso(scadenza),
      period_from: iso(inizio),
      period_to: iso(fine),
      payment_method: ultima?.payment_method || '',
    });
    setDialogOpen(true);
    toast.info(`Fattura compilata dal contratto ${c.operator || ''}: controlla e salva`);
  }, [contracts, billings, loading, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = searchParams.get('fattura');
    if (!id || !billings.length) return;
    const b = billings.find((x) => x.id === id);
    setSearchParams({}, { replace: true });
    if (!b) {
      toast.error('Fattura non trovata');
      return;
    }
    setFilterStatus('all');
    setSearchTerm(b.client_name || '');
    setExpandedClients({ [b.client_id || 'unknown']: true });
    setEvidenziata(b.id);
    setTimeout(() => document.querySelector(`[data-testid="billing-row-${b.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  }, [billings, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredBillings = billings
    .filter(b => {
      const matchesSearch = `${b.client_name || ''} ${b.contract_number || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || b.status === filterStatus;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (amountSort === 'asc') return (a.amount || 0) - (b.amount || 0);
      if (amountSort === 'desc') return (b.amount || 0) - (a.amount || 0);
      if (dateSort === 'asc') return new Date(a.billing_date || 0) - new Date(b.billing_date || 0);
      if (dateSort === 'desc') return new Date(b.billing_date || 0) - new Date(a.billing_date || 0);
      if (dueDateSort === 'asc') return new Date(a.due_date || 0) - new Date(b.due_date || 0);
      if (dueDateSort === 'desc') return new Date(b.due_date || 0) - new Date(a.due_date || 0);
      if (statusSort === 'asc') return (a.status || '').localeCompare(b.status || '');
      if (statusSort === 'desc') return (b.status || '').localeCompare(a.status || '');
      return 0;
    });

  const clientContracts = contracts.filter(c => c.client_id === formData.client_id);

  const totalDaPagare = billings.filter(b => b.status === 'da_pagare').reduce((s, b) => s + (b.amount || 0), 0);
  const totalPagato = billings.filter(b => b.status === 'pagato').reduce((s, b) => s + (b.amount || 0), 0);

  const exportToExcel = () => {
    const headers = ['Cliente', 'Operatore', 'Data Fattura', 'Scadenza', 'Importo', 'Metodo Pagamento', 'Stato', 'Data Pagamento', 'Note'];
    const rows = filteredBillings.map(b => [
      b.client_name || '',
      b.contract_number || '',
      b.billing_date ? new Date(b.billing_date).toLocaleDateString('it-IT') : '',
      b.due_date ? new Date(b.due_date).toLocaleDateString('it-IT') : '',
      (b.amount || 0).toFixed(2).replace('.', ','),
      paymentMethods[b.payment_method] || b.payment_method || '',
      statusLabels[b.status] || b.status,
      b.status === 'pagato' && b.payment_date ? new Date(b.payment_date).toLocaleDateString('it-IT') : '',
      b.notes || ''
    ]);
    const csvContent = [headers.join(';'), ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fatture_telecom_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Esportazione completata');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="telecom-billing-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Fatturazione</h1>
          <p className="text-slate-600 mt-1">Gestisci le fatture dei contratti telefonici</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleAutoGenerate} disabled={autoGenerating} data-testid="auto-generate-billings-btn" title="Genera automaticamente le prossime fatture per quelle scadute">
            {autoGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Genera Prossime
          </Button>
          <Button variant="outline" onClick={() => setEmailConfigOpen(true)} data-testid="email-config-btn">
            <Settings className="w-4 h-4 mr-2" />Configurazione Email
          </Button>
          <Button variant="outline" onClick={exportToExcel} disabled={billings.length === 0} data-testid="export-billing-btn">
            <Download className="w-4 h-4 mr-2" />Esporta Excel
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { setEditingBilling(null); setFormData(emptyBilling); setClientSearch(''); }} data-testid="add-billing-btn">
                <Plus className="w-4 h-4 mr-2" />Nuova Fattura
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingBilling ? 'Modifica Fattura' : 'Nuova Fattura'}</DialogTitle>
              <DialogDescription>Inserisci i dati della fattura</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Input
                  placeholder="Cerca cliente per nome, cognome, ragione sociale, codice fiscale, P.IVA..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="mb-1"
                  data-testid="billing-client-search"
                />
                {clientSearch && (
                  <p className="text-xs text-slate-500 mb-1">
                    {filteredClients.length} {filteredClients.length === 1 ? 'cliente trovato' : 'clienti trovati'}
                    {filteredClients.length > 0 && ' — clicca "Seleziona cliente" per scegliere'}
                  </p>
                )}
                <Select value={formData.client_id} onValueChange={(v) => setFormData({ ...formData, client_id: v, contract_id: '' })}>
                  <SelectTrigger data-testid="billing-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>
                    {filteredClients.length === 0 ? (
                      <div className="px-2 py-3 text-sm text-slate-400 text-center">Nessun cliente trovato</div>
                    ) : filteredClients.map((c) => {
                      const display = c.business_name || c.ragione_sociale || `${c.last_name || ''} ${c.first_name || ''}`.trim() || c.first_name || '—';
                      return (
                        <SelectItem key={c.id} value={c.id}>
                          {c.client_code ? <span className="text-xs text-slate-400 mr-2 font-mono">{c.client_code}</span> : null}
                          {display}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contratto</Label>
                <Select value={formData.contract_id} onValueChange={(v) => setFormData({ ...formData, contract_id: v })} disabled={!formData.client_id}>
                  <SelectTrigger data-testid="billing-contract-select">
                    <SelectValue placeholder={formData.client_id ? "Seleziona contratto" : "Prima seleziona un cliente"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clientContracts.map((c) => (<SelectItem key={c.id} value={c.id}>{c.operator || 'Contratto'} - {c.contract_type}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Importo (€)</Label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} placeholder="0.00" data-testid="billing-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Stato</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger data-testid="billing-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Data Fattura</Label>
                  <DateInput value={formData.billing_date} onChange={(val) => setFormData({ ...formData, billing_date: val })} data-testid="billing-date" placeholder="gg/mm/aaaa" />
                </div>
                <div className="space-y-2">
                  <Label>Scadenza</Label>
                  <DateInput value={formData.due_date} onChange={(val) => setFormData({ ...formData, due_date: val })} data-testid="billing-due-date" placeholder="gg/mm/aaaa" />
                </div>
              </div>
              {/* Periodo di riferimento */}
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 space-y-2">
                <Label className="text-xs text-violet-700 font-semibold uppercase tracking-wide">
                  📅 Periodo di riferimento della fattura
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Dal mese</Label>
                    <Select
                      value={formData.period_from}
                      onValueChange={(v) => {
                        // Se "to" è vuoto o precedente, allinealo
                        const next = { ...formData, period_from: v };
                        if (!formData.period_to || formData.period_to < v) {
                          next.period_to = getLastDayOfMonth(v);
                        }
                        setFormData(next);
                      }}
                    >
                      <SelectTrigger data-testid="billing-period-from"><SelectValue placeholder="Seleziona mese" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {MONTH_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Al mese</Label>
                    <Select
                      value={formData.period_to ? formData.period_to.slice(0, 7) + '-01' : ''}
                      onValueChange={(v) => {
                        // Salva ultimo giorno del mese scelto
                        setFormData({ ...formData, period_to: getLastDayOfMonth(v) });
                      }}
                    >
                      <SelectTrigger data-testid="billing-period-to"><SelectValue placeholder="Seleziona mese" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {MONTH_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {(formData.period_from || formData.period_to) && (
                  <p className="text-xs text-violet-700 font-medium mt-1">
                    💡 Periodo: <strong>{formatPeriodRange(formData.period_from, formData.period_to)}</strong>
                  </p>
                )}
              </div>
              {formData.status === 'pagato' && (
                <div className="space-y-2">
                  <Label>Data Pagamento</Label>
                  <DateInput value={formData.payment_date} onChange={(val) => setFormData({ ...formData, payment_date: val })} data-testid="billing-payment-date" placeholder="vuoto = oggi" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Metodo Pagamento</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger data-testid="billing-payment-select"><SelectValue placeholder="Seleziona metodo" /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(paymentMethods).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} data-testid="billing-notes" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setClientSearch(''); }}>Annulla</Button>
                <Button type="submit" className="bg-violet-600 hover:bg-violet-700" data-testid="save-billing-btn">
                  {editingBilling ? 'Salva Modifiche' : 'Crea Fattura'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Da Pagare</p>
            <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalDaPagare)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Pagato</p>
            <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalPagato)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-sm text-slate-500">Totale Fatture</p>
            <p className="text-2xl font-bold text-slate-900">{billings.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca per cliente, operatore..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-billing" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                <SelectValue placeholder="Stato" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {Object.entries(statusLabels).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Billing Table - Grouped by Client */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : filteredBillings.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm || filterStatus !== 'all' ? 'Nessuna fattura trovata' : 'Nessuna fattura registrata'}
            </div>
          ) : (() => {
            // Group by client
            const grouped = {};
            filteredBillings.forEach(b => {
              const key = b.client_id || 'unknown';
              if (!grouped[key]) grouped[key] = { name: b.client_name || '-', billings: [] };
              grouped[key].billings.push(b);
            });
            const toggleClient = (clientId) => setExpandedClients(prev => ({ ...prev, [clientId]: !prev[clientId] }));

            // Ordina i gruppi cliente e pagina a 30 per pagina
            const sortedEntries = Object.entries(grouped).sort((a, b) => {
              const lastA = a[1].billings.sort((x, y) => new Date(y.billing_date || 0) - new Date(x.billing_date || 0))[0];
              const lastB = b[1].billings.sort((x, y) => new Date(y.billing_date || 0) - new Date(x.billing_date || 0))[0];
              const dateA = lastA ? new Date(lastA.billing_date || 0) : new Date(0);
              const dateB = lastB ? new Date(lastB.billing_date || 0) : new Date(0);
              return clientSort === 'desc' ? dateB - dateA : dateA - dateB;
            });
            const totalClientGroups = sortedEntries.length;
            const totalPages = Math.ceil(totalClientGroups / ITEMS_PER_PAGE);
            const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
            const endIndex = startIndex + ITEMS_PER_PAGE;
            const paginatedEntries = sortedEntries.slice(startIndex, endIndex);

            return (
              <div>
                {/* Header fisso colonne */}
                <div className="flex items-center px-4 py-2 bg-slate-100 border-b text-xs font-medium text-slate-500 uppercase">
                  <div className="w-8 flex-shrink-0"></div>
                  <div className="w-8 flex-shrink-0 ml-2"></div>
                  <div className="flex-1 min-w-0">Cliente</div>
                  <div className="w-[100px] text-center flex-shrink-0">Fatture</div>
                  <div className="w-[130px] text-center flex-shrink-0 cursor-pointer select-none hover:text-violet-600" onClick={() => setClientSort(prev => prev === 'desc' ? 'asc' : 'desc')}>
                    <span className="flex items-center justify-center gap-1">Ultima {clientSort === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}</span>
                  </div>
                  <div className="w-[100px] text-center flex-shrink-0">Da Pagare</div>
                  <div className="w-[100px] text-right flex-shrink-0">Totale</div>
                </div>
                {paginatedEntries.map(([clientId, group]) => {
                  const isExpanded = expandedClients[clientId];
                  const totalAmount = group.billings.reduce((s, b) => s + (b.amount || 0), 0);
                  const daPagare = group.billings.filter(b => b.status === 'da_pagare');
                  const lastBilling = group.billings.sort((a, b) => new Date(b.billing_date || 0) - new Date(a.billing_date || 0))[0];
                  return (
                    <div key={clientId} className="border-b last:border-b-0">
                      {/* Client Header Row */}
                      <div
                        className="flex items-center px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleClient(clientId)}
                        data-testid={`client-group-${clientId}`}
                      >
                        <div className="w-8 flex-shrink-0">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                        </div>
                        <div className="w-8 flex-shrink-0 ml-2">
                          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                            <User className="w-4 h-4 text-violet-600" />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="font-semibold text-slate-900 text-sm">{group.name}</span>
                        </div>
                        <div className="w-[100px] text-center flex-shrink-0">
                          <span className="text-sm text-slate-600">{group.billings.length}</span>
                        </div>
                        <div className="w-[130px] text-center flex-shrink-0">
                          <span className="text-xs text-slate-400">{lastBilling ? formatDate(lastBilling.billing_date) : '-'}</span>
                        </div>
                        <div className="w-[100px] text-center flex-shrink-0">
                          {daPagare.length > 0 ? (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">{daPagare.length}</Badge>
                          ) : <span className="text-xs text-slate-400">-</span>}
                        </div>
                        <div className="w-[100px] text-right flex-shrink-0">
                          <span className="font-bold text-sm text-violet-700">{formatCurrency(totalAmount)}</span>
                        </div>
                      </div>
                      {/* Expanded Billings */}
                      {isExpanded && (
                        <div className="bg-slate-50/50 overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-slate-100/80 border-y">
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[12%]">Operatore</th>
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[13%]">Data</th>
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[13%]">Scadenza</th>
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[12%]">Importo</th>
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[11%]">Pagato il</th>
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[11%]">Metodo</th>
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[13%]">Note</th>
                                <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-2 w-[12%]">Stato</th>
                                <th className="px-4 py-2 w-[7%]"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {group.billings.map(billing => (
                                <tr key={billing.id} className={`border-b last:border-b-0 hover:bg-white transition-colors ${evidenziata === billing.id ? 'bg-yellow-100 ring-2 ring-inset ring-yellow-400' : ''}`} data-testid={`billing-row-${billing.id}`}>
                                  <td className="px-4 py-2 text-sm">{billing.contract_number || '-'}</td>
                                  <td className="px-4 py-2 text-sm">
                                    <div>{formatDate(billing.billing_date)}</div>
                                    {(billing.period_from || billing.period_to) && (
                                      <div className="text-xs text-violet-600 font-medium mt-0.5" data-testid={`billing-period-${billing.id}`}>
                                        📅 {formatPeriodRange(billing.period_from, billing.period_to)}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm">{formatDate(billing.due_date)}</td>
                                  <td className="px-4 py-2 text-sm font-medium text-violet-600">{formatCurrency(billing.amount)}</td>
                                  <td className="px-4 py-2 text-sm text-emerald-700" data-testid={`billing-paid-date-${billing.id}`}>{billing.status === 'pagato' ? formatDate(billing.payment_date) : '-'}</td>
                                  <td className="px-4 py-2 text-sm text-slate-500">{paymentMethods[billing.payment_method] || '-'}</td>
                                  <td className="px-4 py-2 text-sm text-slate-500 truncate max-w-[150px]">{billing.notes || '-'}</td>
                                  <td className="px-4 py-2">
                                    {billing.status === 'da_pagare' ? (
                                      <Button
                                        size="sm" variant="outline"
                                        className="h-6 px-2 text-xs border-amber-300 text-amber-700 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700"
                                        onClick={(e) => { e.stopPropagation(); handleMarkPaid(billing); }}
                                        data-testid={`mark-paid-${billing.id}`}
                                        title="Stato: Da Pagare — clicca per marcare come Pagato"
                                      >
                                        Da Pagare →
                                      </Button>
                                    ) : getStatusBadge(billing.status)}
                                  </td>
                                  <td className="px-4 py-2">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => e.stopPropagation()} data-testid={`billing-menu-${billing.id}`}>
                                          <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        {billing.status === 'da_pagare' && (
                                          <>
                                            <DropdownMenuItem onClick={() => handleMarkPaid(billing, true)}>
                                              <Receipt className="w-4 h-4 mr-2" />Pagato + Genera Prossima
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={() => handleMarkPaid(billing, false)}>
                                              <Receipt className="w-4 h-4 mr-2" />Solo Pagato
                                            </DropdownMenuItem>
                                          </>
                                        )}
                                        {billing.status === 'pagato' && (
                                          <DropdownMenuItem onClick={() => handleMarkPaid(billing, true)} data-testid={`generate-next-${billing.id}`}>
                                            <Receipt className="w-4 h-4 mr-2" />Genera Fattura Successiva
                                          </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onClick={() => handleSendEmail(billing)}>
                                          <Send className="w-4 h-4 mr-2" />Invia Email
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleClone(billing)}>
                                          <Copy className="w-4 h-4 mr-2" />Clona
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleEdit(billing)}>
                                          <Pencil className="w-4 h-4 mr-2" />Modifica
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleDelete(billing.id)} className="text-red-600">
                                          <Trash2 className="w-4 h-4 mr-2" />Elimina
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t">
                    <span className="text-xs text-slate-500">
                      Visualizzando clienti {startIndex + 1}-{Math.min(endIndex, totalClientGroups)} di {totalClientGroups}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                        Precedente
                      </Button>
                      <span className="text-xs text-slate-600">Pagina {currentPage} di {totalPages}</span>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                        Successiva
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Client Details Dialog */}
      <Dialog open={clientDetailsOpen} onOpenChange={setClientDetailsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-violet-600" />
              Dettagli Cliente
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient && (
            <div className="space-y-4 mt-4">
              {!editingClient ? (
                <>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-lg">{selectedClient.first_name} {selectedClient.last_name}</p>
                        <p className="text-sm text-slate-500">{selectedClient.client_code}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-2 pt-2 border-t">
                      {selectedClient.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span>{selectedClient.email}</span>
                        </div>
                      )}
                      {selectedClient.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span>{selectedClient.phone}</span>
                        </div>
                      )}
                      {(selectedClient.address || selectedClient.city) && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{[selectedClient.address, selectedClient.city, selectedClient.postal_code].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setClientDetailsOpen(false)}>Chiudi</Button>
                    <Button onClick={() => setEditingClient(true)} className="bg-violet-600 hover:bg-violet-700">
                      <Pencil className="w-4 h-4 mr-2" />Modifica
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nome</Label>
                        <Input 
                          value={clientFormData.first_name} 
                          onChange={(e) => setClientFormData({...clientFormData, first_name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Cognome</Label>
                        <Input 
                          value={clientFormData.last_name} 
                          onChange={(e) => setClientFormData({...clientFormData, last_name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input 
                          value={clientFormData.email} 
                          onChange={(e) => setClientFormData({...clientFormData, email: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Telefono</Label>
                        <Input 
                          value={clientFormData.phone} 
                          onChange={(e) => setClientFormData({...clientFormData, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Indirizzo</Label>
                      <Input 
                        value={clientFormData.address} 
                        onChange={(e) => setClientFormData({...clientFormData, address: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Città</Label>
                        <Input 
                          value={clientFormData.city} 
                          onChange={(e) => setClientFormData({...clientFormData, city: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>CAP</Label>
                        <Input 
                          value={clientFormData.postal_code} 
                          onChange={(e) => setClientFormData({...clientFormData, postal_code: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button variant="outline" onClick={() => setEditingClient(false)}>Annulla</Button>
                    <Button onClick={handleSaveClient} className="bg-emerald-600 hover:bg-emerald-700">
                      Salva Modifiche
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Config Dialog */}
      <Dialog open={emailConfigOpen} onOpenChange={setEmailConfigOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurazione Automazione Email Telefonia
            </DialogTitle>
            <DialogDescription>
              Configura i parametri per l'invio automatico delle email di promemoria scadenza contratti telefonici.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-base font-medium">Automazione Attiva</Label>
                <p className="text-sm text-slate-500">Abilita l'invio automatico giornaliero delle email</p>
              </div>
              <Switch
                checked={emailConfig.enabled}
                onCheckedChange={(checked) => setEmailConfig({...emailConfig, enabled: checked})}
                data-testid="telecom-email-enabled"
              />
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-600" />
                    Orario di Invio Giornaliero
                  </Label>
                  <p className="text-sm text-slate-500 mt-1">L'invio automatico verrà eseguito ogni giorno a questo orario (fuso orario: Europe/Rome)</p>
                </div>
                <Input
                  type="time"
                  value={emailConfig.daily_send_time || "09:00"}
                  onChange={(e) => setEmailConfig({...emailConfig, daily_send_time: e.target.value})}
                  className="w-32 text-center font-mono text-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email Mittente</Label>
                <Input value={emailConfig.sender_email} disabled className="bg-slate-100" />
                <p className="text-xs text-slate-500">Forzato: no-reply@jvservice.it</p>
              </div>
              <div className="space-y-2">
                <Label>Nome Mittente</Label>
                <Input
                  value={emailConfig.sender_name}
                  onChange={(e) => setEmailConfig({...emailConfig, sender_name: e.target.value})}
                  placeholder="JV Service - Ufficio Telefonia"
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-base font-medium">Giorni Prima della Scadenza</Label>
              <p className="text-sm text-slate-500">Configura quanti giorni prima della scadenza contratto inviare la notifica</p>
              <div className="max-w-xs">
                <div className="space-y-2 p-3 border rounded-lg">
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-violet-600" />
                    <Label>Contratti Telefonici</Label>
                  </div>
                  <Input
                    type="number"
                    min={1}
                    max={90}
                    value={emailConfig.days_before_expiry}
                    onChange={(e) => setEmailConfig({...emailConfig, days_before_expiry: parseInt(e.target.value) || 30})}
                  />
                  <p className="text-xs text-slate-500">giorni prima</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                <Label className="text-base font-medium">Template Email</Label>
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Oggetto Email <span className="text-xs font-normal text-slate-500">(supporta variabili)</span>
                </Label>
                <Input
                  value={emailConfig.email_subject}
                  onChange={(e) => setEmailConfig({...emailConfig, email_subject: e.target.value})}
                  placeholder="Scadenza Contratto - {operator} - JV Service"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Corpo del Messaggio <span className="text-xs font-normal text-slate-500">(personalizzabile)</span>
                </Label>
                <Textarea
                  value={emailConfig.email_body}
                  onChange={(e) => setEmailConfig({...emailConfig, email_body: e.target.value})}
                  placeholder="Gentile {client_name},&#10;&#10;le ricordiamo che il suo contratto {contract_type} con {operator} scadrà il giorno {expiry_date}."
                  rows={10}
                  className="font-mono text-sm leading-relaxed"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border">
                <p className="text-sm font-medium text-slate-700 mb-2">Variabili disponibili:</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{client_name}'}</code>
                    <span className="text-slate-500">Nome cliente</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{operator}'}</code>
                    <span className="text-slate-500">Operatore telefonico</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{contract_type}'}</code>
                    <span className="text-slate-500">Tipo contratto</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{expiry_date}'}</code>
                    <span className="text-slate-500">Data scadenza</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{days_remaining}'}</code>
                    <span className="text-slate-500">Giorni rimanenti</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="bg-violet-100 text-violet-700 px-2 py-0.5 rounded">{'{phone_number}'}</code>
                    <span className="text-slate-500">Numero in servizio</span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-2">Firma Email (inclusa automaticamente):</p>
                <div className="text-sm text-slate-600 space-y-1">
                  <p className="font-semibold text-slate-800">JV SERVICE SRLS</p>
                  <p>Via Fabio Filzi, 39 L, 59100 Prato (PO) – Italy</p>
                  <p>Cell: +39 375 675 2552</p>
                  <p>Email: operatore@tlcne.it</p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEmailConfigOpen(false)}>Annulla</Button>
            <Button onClick={saveEmailConfig} disabled={configLoading}>
              {configLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Salva Configurazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TelecomBilling;
